import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  CUSTOM_REPORT_FIELDS,
  DAYS_OF_WEEK,
  FIELD_OPERATORS,
  REPORT_FREQUENCY
} from 'constants/customReportFields';
import api from 'helpers/api';
import {
  formatDate,
  getSlaStatusOptions,
  getTicketPriorities,
  getTicketStatus
} from 'helpers/utils';
import { useCustomReportContext } from 'providers/CustomReportProvider';
import { useEffect, useState } from 'react';
import { Badge, Card, Col, Row } from 'react-bootstrap';
import ContactsService from 'services/Contacts.service';
import ServiceCatalogService from 'services/ServiceCatalog.service';
import ServiceGroupService from 'services/ServiceGroup.service';
import TypeTicketsService from 'services/TypeTickets.service';

const SummaryStep = () => {
  const { reportData } = useCustomReportContext();
  const { schedule } = reportData;

  // Estados para dados de relacionamento
  const [agents, setAgents] = useState([]);
  const [typesTicket, setTypesTicket] = useState([]);
  const [serviceGroups, setServiceGroups] = useState([]);
  const [serviceCatalogs, setServiceCatalogs] = useState([]);
  const [ticketOrigins, setTicketOrigins] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [assets, setAssets] = useState([]);

  // Estado para controlar se os dados foram carregados
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const http = api();

  // Carrega dados de relacionamento
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Agentes - usando listSummaryData do api.js
        const agentsData = await http.agents.listSummaryData();
        if (agentsData && Array.isArray(agentsData)) {
          setAgents(
            agentsData.map(agent => ({
              id: agent.id,
              name: `${agent.firstName} ${agent.lastName}`.trim()
            }))
          );
        }

        // Tipos de ticket - usando list do TypeTicketsService
        const typesData = await TypeTicketsService.list(
          'page=0&size=1000&includeInactives=false'
        );
        if (
          typesData &&
          typesData.content &&
          Array.isArray(typesData.content)
        ) {
          setTypesTicket(
            typesData.content.map(type => ({
              id: type.id,
              name: type.name
            }))
          );
        }

        // Grupos de serviço - usando listSummaryData do ServiceGroupService
        const groupsData = await ServiceGroupService.listSummaryData(
          'page=0&size=1000&includeInactives=false'
        );
        if (
          groupsData &&
          groupsData.content &&
          Array.isArray(groupsData.content)
        ) {
          setServiceGroups(
            groupsData.content.map(group => ({
              id: group.id,
              name: group.name
            }))
          );
        }

        // Catálogos de serviço - usando listInTreeView do ServiceCatalogService
        const catalogsData = await ServiceCatalogService.listInTreeView();
        if (catalogsData && Array.isArray(catalogsData)) {
          setServiceCatalogs(catalogsData);
        }

        // Origens de ticket
        const originsData = await http.ticketOrigins.list();
        if (originsData && Array.isArray(originsData)) {
          setTicketOrigins(
            originsData.map(item => ({
              id: item.ticketOrigin,
              name: item.description
            }))
          );
        }

        // Assets
        const assetsData = await http.assets.list(
          'page=0&size=1000&includeInactives=false'
        );
        if (
          assetsData &&
          assetsData.content &&
          Array.isArray(assetsData.content)
        ) {
          setAssets(
            assetsData.content.map(asset => ({
              id: asset.id,
              name: asset.name
            }))
          );
        }

        // Clientes
        const customersData = await http.customers.list('size=1000');
        if (
          customersData &&
          customersData.content &&
          Array.isArray(customersData.content)
        ) {
          setCustomers(
            customersData.content.map(customer => ({
              id: customer.id,
              name: customer.name
            }))
          );
        }

        // Contatos
        const contactsData = await ContactsService.list('size=1000');
        if (
          contactsData &&
          contactsData.content &&
          Array.isArray(contactsData.content)
        ) {
          setContacts(
            contactsData.content.map(contact => ({
              id: contact.id,
              name: contact.name
            }))
          );
        }
      } catch (error) {
        console.error('Erro ao carregar dados de relacionamento:', error);
      } finally {
        // Marca que os dados foram carregados (mesmo se houver erro)
        setIsDataLoaded(true);
      }
    };

    fetchData();
  }, []);

  // Monitora mudanças nos filtros para garantir formatação em tempo real
  useEffect(() => {
    // Força re-render quando os filtros mudam para atualizar a formatação
    // Os dados já estão carregados, então a formatação acontecerá automaticamente
  }, [reportData.filters]);

  const getFieldDisplayName = fieldId => {
    const field = CUSTOM_REPORT_FIELDS.find(f => f.fieldId === fieldId);
    return field ? field.displayName : fieldId;
  };

  const getOperatorLabel = (fieldId, operatorValue) => {
    const field = CUSTOM_REPORT_FIELDS.find(f => f.fieldId === fieldId);
    if (!field) return operatorValue;

    const operators = FIELD_OPERATORS[field.dataType] || [];
    const operator = operators.find(op => op.value === operatorValue);
    return operator ? operator.label : operatorValue;
  };

  const getFrequencyLabel = value => {
    const freq = REPORT_FREQUENCY.find(f => f.value === value);
    return freq ? freq.label : value;
  };

  const getDaysLabel = scheduleDetails => {
    if (!scheduleDetails) return '';
    const dayValues = scheduleDetails.split(',').filter(Boolean);
    return dayValues
      .map(val => {
        const day = DAYS_OF_WEEK.find(d => d.value === val);
        return day ? day.label : val;
      })
      .join(', ');
  };

  // Função para verificar se é um valor de data dinâmico
  const isDynamicDateValue = value => {
    if (!value || typeof value !== 'string') return false;
    const dynamicValues = [
      // Valores em português
      'hoje',
      'ontem',
      'esta semana',
      'semana passada',
      'este mês',
      'mês passado',
      // Valores em inglês (do backend)
      'today',
      'yesterday',
      'this_week',
      'last_7_days',
      'last_15_days',
      'this_month',
      'last_30_days',
      '7_days_ago',
      '15_days_ago',
      '30_days_ago'
    ];
    return dynamicValues.includes(value.toLowerCase());
  };

  // Função para verificar se é uma string de data ISO
  const isISODateString = value => {
    if (!value || typeof value !== 'string') return false;
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;
    return isoDateRegex.test(value);
  };

  // Função para traduzir valores dinâmicos de inglês para português
  const translateDynamicValue = value => {
    const translations = {
      today: 'hoje',
      yesterday: 'ontem',
      this_week: 'esta semana',
      last_7_days: 'últimos 7 dias',
      last_15_days: 'últimos 15 dias',
      this_month: 'este mês',
      last_30_days: 'últimos 30 dias',
      '7_days_ago': '7 dias atrás',
      '15_days_ago': '15 dias atrás',
      '30_days_ago': '30 dias atrás'
    };
    return translations[value.toLowerCase()] || value;
  };

  // Função para formatar valores de catálogo de serviço (caso especial)
  const formatServiceCatalogValue = value => {
    if (!value) return '';

    // Se é uma string com vírgulas, pode ser um caminho hierárquico (como "1,1,1")
    if (typeof value === 'string' && value.includes(',')) {
      const ids = value.split(',').map(id => parseInt(id.trim()));

      // Tenta interpretar como um caminho hierárquico único primeiro
      const hierarchicalPath = formatServiceCatalogPath(ids);

      if (hierarchicalPath) {
        // É um caminho hierárquico único
        return hierarchicalPath;
      } else {
        // São múltiplos catálogos, busca cada um individualmente
        const paths = ids
          .map(id => {
            const path = formatServiceCatalogPath([id]);
            return path;
          })
          .filter(path => path);
        return paths.join(', ');
      }
    }

    // Se é um array de IDs
    if (Array.isArray(value)) {
      return formatServiceCatalogPath(value);
    }

    // Se é um ID único
    const numericValue = parseInt(value);
    const singlePath = formatServiceCatalogPath([numericValue]);
    return singlePath || value;
  };

  // Função para formatar o caminho do catálogo de serviço
  const formatServiceCatalogPath = ids => {
    if (
      !ids ||
      ids.length === 0 ||
      !serviceCatalogs ||
      serviceCatalogs.length === 0
    ) {
      return '';
    }

    // Função recursiva para buscar o caminho na árvore
    const findPathInTree = (nodes, targetIds, level = 0, currentPath = []) => {
      if (level >= targetIds.length || !nodes || nodes.length === 0) {
        return level === targetIds.length ? currentPath.join(' / ') : null;
      }

      const targetId = targetIds[level];
      const node = nodes.find(n => n.value === targetId);
      if (!node) {
        return null;
      }

      // Adiciona o título do nó atual ao caminho
      const newPath = [...currentPath, node.title];

      // Se é o último nível, retorna o caminho completo
      if (level === targetIds.length - 1) {
        return newPath.join(' / ');
      }

      // Continua procurando nos filhos
      const childResult = findPathInTree(
        node.children || [],
        targetIds,
        level + 1,
        newPath
      );

      return childResult;
    };

    const pathResult = findPathInTree(serviceCatalogs, ids);

    if (pathResult) {
      return pathResult;
    }

    return '';
  };

  // Função para formatar valores de relacionamento
  const formatJoinValue = (fieldId, value) => {
    if (!value) return '';

    // Se os dados ainda não foram carregados, mostra um placeholder elegante
    if (!isDataLoaded) {
      return (
        <span className="text-muted small d-flex align-items-center">
          <div
            className="spinner-border spinner-border-sm me-2"
            role="status"
            style={{ width: '0.8rem', height: '0.8rem' }}
          >
            <span className="visually-hidden">Carregando...</span>
          </div>
          Carregando...
        </span>
      );
    }

    // Caso especial para catálogo de serviço - sempre usa formatação de árvore
    if (fieldId === 'TICKET_SERVICE_CATALOG') {
      const catalogValue = formatServiceCatalogValue(value);
      // Se não conseguiu formatar e não há dados carregados, retorna o valor original
      if (!catalogValue && !isDataLoaded) {
        return value;
      }
      return catalogValue;
    }

    // Mapeamento de campos para suas respectivas listas
    const fieldToDataMap = {
      TICKET_ASSIGNEE: agents,
      TICKET_TYPE: typesTicket,
      TICKET_SERVICE_GROUP: serviceGroups,
      TICKET_ASSET: assets,
      TICKET_SOURCE_CHANNEL: ticketOrigins,
      TICKET_CUSTOMER: customers,
      TICKET_REQUESTER: contacts
    };

    const dataList = fieldToDataMap[fieldId];

    if (!dataList || !Array.isArray(dataList) || dataList.length === 0) {
      return value;
    }

    // Verifica se é um valor com múltiplos IDs (separados por vírgula)
    if (typeof value === 'string' && value.includes(',')) {
      const ids = value.split(',').map(id => parseInt(id.trim()));
      const names = ids.map(id => {
        const item = dataList.find(item => item.id == id);
        return item ? item.name : id;
      });
      return names.join(', ');
    }

    // Para valores únicos - converte para número se necessário
    const numericValue = typeof value === 'string' ? parseInt(value) : value;
    const item = dataList.find(item => item.id == numericValue);
    return item ? item.name : value;
  };

  // Função para formatar valores de filtros
  const formatFilterValue = filter => {
    if (!filter.value) return '';

    const field = CUSTOM_REPORT_FIELDS.find(f => f.fieldId === filter.fieldId);
    if (!field) return filter.value;

    // Formatação para campos boolean
    if (field.dataType === 'boolean') {
      return filter.value === 'true' || filter.value === true ? 'Sim' : 'Não';
    }

    // Formatação para campos enum
    if (field.isEnum) {
      if (field.fieldId === 'TICKET_STATUS') {
        const status = getTicketStatus().find(s => s.id === filter.value);
        return status ? status.name : filter.value;
      }

      if (field.fieldId === 'TICKET_PRIORITY') {
        const priority = getTicketPriorities().find(p => p.id === filter.value);
        return priority ? priority.name : filter.value;
      }

      if (
        field.fieldId === 'TICKET_STATUS_SLA_RESPONSE' ||
        field.fieldId === 'TICKET_STATUS_SLA_SOLUTION'
      ) {
        const slaStatus = getSlaStatusOptions().find(
          s => s.id === filter.value
        );
        if (slaStatus) {
          return (
            <div className="d-flex align-items-center">
              <div
                className="rounded-circle me-2"
                style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: slaStatus.color
                }}
              />
              <span style={{ color: slaStatus.color, fontWeight: '500' }}>
                {slaStatus.name}
              </span>
            </div>
          );
        }
        return filter.value;
      }
    }

    // Formatação para campos datetime
    if (field.dataType === 'datetime') {
      // Verifica se é um valor dinâmico primeiro
      if (isDynamicDateValue(filter.value)) {
        return translateDynamicValue(filter.value);
      }

      if (filter.operator === 'BETWEEN' && filter.value.includes('|')) {
        const [start, end] = filter.value.split('|');
        return `${formatDate(start)} - ${formatDate(end)}`;
      }
      return formatDate(filter.value);
    }

    // Formatação para campos duration
    if (field.dataType === 'duration') {
      const hours = parseFloat(filter.value);
      if (isNaN(hours)) return filter.value;

      const wholeHours = Math.floor(hours);
      const minutes = Math.round((hours - wholeHours) * 60);

      if (wholeHours === 0) {
        return `${minutes}min`;
      } else if (minutes === 0) {
        return `${wholeHours}h`;
      } else {
        return `${wholeHours}h ${minutes}min`;
      }
    }

    // Formatação para campos com join - sempre formata usando dados carregados
    if (field.hasJoin) {
      // Se tem displayValue e os dados ainda não foram carregados, usa displayValue
      if (filter.displayValue && !isDataLoaded) {
        return filter.displayValue;
      }

      // Sempre formata usando os dados carregados para garantir consistência
      const joinValue = formatJoinValue(filter.fieldId, filter.value);
      // Se retornou um JSX (loading), retorna como está
      if (typeof joinValue === 'object') {
        return joinValue;
      }
      return joinValue;
    }

    // Se o filtro tem displayValue e os dados não estão carregados, prioriza ele
    if (filter.displayValue && !isDataLoaded) {
      // Se é um valor dinâmico de data, traduz para português
      if (isDynamicDateValue(filter.displayValue)) {
        return translateDynamicValue(filter.displayValue);
      }

      // Se é um operador BETWEEN para datas
      if (filter.operator === 'BETWEEN' && filter.displayValue.includes('|')) {
        const [startDate, endDate] = filter.displayValue.split('|');

        // Se ambos são valores dinâmicos, traduz ambos
        if (isDynamicDateValue(startDate) && isDynamicDateValue(endDate)) {
          return `${translateDynamicValue(startDate)} - ${translateDynamicValue(
            endDate
          )}`;
        }

        // Se ambos são datas ISO, formata
        if (isISODateString(startDate) && isISODateString(endDate)) {
          return `${formatDate(startDate)} - ${formatDate(endDate)}`;
        }

        return filter.displayValue;
      }

      // Para outros operadores de data
      if (isISODateString(filter.displayValue)) {
        return formatDate(filter.displayValue);
      }

      return filter.displayValue;
    }

    return filter.value;
  };

  return (
    <>
      <h5 className="mb-2">Finalização</h5>
      <p className="text-muted mb-4 fs-11">
        Revise a configuração do seu relatório antes de salvar. Você pode voltar
        a qualquer etapa para fazer alterações.
      </p>

      {/* Basic Information */}
      <Card className="shadow-sm mb-3">
        <Card.Body className="p-4">
          <Card.Title className="mb-4">Informações Básicas</Card.Title>
          <Row>
            <Col xs={12} sm={6} md={3} className="mb-3">
              <p className="text-muted mb-1 small">Nome do Relatório</p>
              <h6 className="fw-semibold mb-0">
                {reportData.name || 'Não especificado'}
              </h6>
            </Col>
            <Col xs={12} sm={6} md={3} className="mb-3">
              <p className="text-muted mb-1 small">Status</p>
              <Badge
                className="fw-semibold"
                bg={reportData.inactive ? 'danger' : 'success'}
              >
                {reportData.inactive ? 'Inativo' : 'Ativo'}
              </Badge>
            </Col>
            {reportData.description && (
              <Col xs={12} sm={12} md={6} className="mb-3">
                <p className="text-muted mb-1 small">Descrição</p>
                <h6 className="fw-semibold mb-0 text-break">
                  {reportData.description}
                </h6>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {/* Selected Columns */}
      <Card className="shadow-sm mb-3">
        <Card.Body className="p-4">
          <Card.Title className="mb-4">
            Colunas Selecionadas ({reportData.columns.length})
          </Card.Title>
          {reportData.columns.length > 0 ? (
            <div className="border rounded p-3 bg-100">
              <Row className="g-2">
                {reportData.columns.map((col, index) => (
                  <Col xs={12} sm={6} md={4} lg={3} key={col.fieldId}>
                    <div className="d-flex align-items-center p-2 bg-body-emphasis rounded border">
                      <Badge bg="primary" className="me-2">
                        {index + 1}
                      </Badge>
                      <span className="small fw-medium text-truncate">
                        {col.displayName}
                      </span>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          ) : (
            <p className="text-muted mb-0">Nenhuma coluna selecionada</p>
          )}
        </Card.Body>
      </Card>

      {/* Filters */}
      <Card className="shadow-sm mb-3">
        <Card.Body className="p-4">
          <Card.Title className="mb-4">
            Filtros ({reportData.filters.length})
          </Card.Title>
          {reportData.filters.length > 0 ? (
            <div className="border rounded p-3 bg-100">
              <div className="table-responsive">
                <table className="table table-sm table-borderless mb-0">
                  <thead>
                    <tr className="small text-muted">
                      <th>Condição</th>
                      <th>Campo</th>
                      <th>Operador</th>
                      <th>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.filters.map((filter, index) => (
                      <tr key={index} className="border-bottom">
                        <td className="py-2">
                          <Badge bg="white" className="small text-dark">
                            {filter.conditionType || 'AND'}
                          </Badge>
                        </td>
                        <td className="py-2">
                          <span className="fw-medium small">
                            {getFieldDisplayName(filter.fieldId)}
                          </span>
                        </td>
                        <td className="py-2">
                          <Badge bg="white" className="small text-dark">
                            {getOperatorLabel(filter.fieldId, filter.operator)}
                          </Badge>
                        </td>
                        <td className="py-2">
                          <span className="fw-medium small text-break">
                            {(() => {
                              const formattedValue = formatFilterValue(filter);
                              // Se é um JSX (loading state), renderiza diretamente
                              if (typeof formattedValue === 'object') {
                                return formattedValue;
                              }
                              // Caso contrário, renderiza como string
                              return formattedValue;
                            })()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-muted mb-0">Nenhum filtro configurado</p>
          )}
        </Card.Body>
      </Card>

      {/* Additional Options */}
      {(reportData.includeTicketNotes ||
        reportData.includeTicketAppointments) && (
        <Card className="shadow-sm mb-3">
          <Card.Body className="p-4">
            <Card.Title className="mb-4">Opções Adicionais</Card.Title>
            <div className="border rounded p-3 bg-light">
              <ul className="list-unstyled mb-0">
                {reportData.includeTicketNotes && (
                  <li className="mb-2">
                    <FontAwesomeIcon
                      icon="check"
                      className="text-success me-2"
                    />
                    <span className="small">Incluir Notas de Ticket</span>
                  </li>
                )}
                {reportData.includeTicketAppointments && (
                  <li className="mb-0">
                    <FontAwesomeIcon
                      icon="check"
                      className="text-success me-2"
                    />
                    <span className="small">
                      Incluir Apontamentos de Ticket
                    </span>
                  </li>
                )}
              </ul>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Scheduling */}
      <Card className="shadow-sm mb-3">
        <Card.Body className="p-4">
          <Card.Title className="mb-4">Agendamento</Card.Title>
          {schedule.enabled ? (
            <Row>
              <Col xs={12} sm={6} md={4} className="mb-3">
                <p className="text-muted mb-1 small">Frequência</p>
                <h6 className="fw-semibold mb-0">
                  {getFrequencyLabel(schedule.frequency)}
                  {schedule.frequency === 'WEEKLY' &&
                    schedule.scheduleDetails && (
                      <span className="text-muted">
                        {' '}
                        ({getDaysLabel(schedule.scheduleDetails)})
                      </span>
                    )}
                </h6>
              </Col>
              <Col xs={12} sm={6} md={4} className="mb-3">
                <p className="text-muted mb-1 small">Horário de Execução</p>
                <h6 className="fw-semibold mb-0">{schedule.executionTime}</h6>
              </Col>
              <Col xs={12} sm={12} md={4} className="mb-3">
                <p className="text-muted mb-1 small">Destinatários</p>
                <h6 className="fw-semibold mb-0 text-break">
                  {schedule.recipients || 'Não especificado'}
                </h6>
              </Col>
              {schedule.emailSubject && (
                <Col xs={12} className="mb-3">
                  <p className="text-muted mb-1 small">Assunto do E-mail</p>
                  <h6 className="fw-semibold mb-0 text-break">
                    {schedule.emailSubject}
                  </h6>
                </Col>
              )}
            </Row>
          ) : (
            <p className="text-muted mb-0">
              <FontAwesomeIcon icon="info-circle" className="me-1" />O
              agendamento automático está desativado para este relatório
            </p>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default SummaryStep;
